import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import SendButton from './SendButton';
import { api } from '../lib/api-client';

export default function UnknownKnowledgeForm({ suggestedQuestion = '', conversationId = null, messageIndex = null, isSubmitted = false }) {
  const [questionText, setQuestionText] = useState(suggestedQuestion);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(isSubmitted);
  const textareaRef = useRef(null);

  // Auto-resize textarea as user types or when suggested question loads
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [questionText]);

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    if (!questionText.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Submit the question
      await api.post('/api/v1/questions', {
        title: questionText,
        conversation_id: conversationId
      });

      // Mark the form as submitted in the conversation
      if (conversationId && messageIndex !== null) {
        try {
          await api.post('/api/v1/chat/form-submitted', {
            conversation_id: conversationId,
            message_index: messageIndex
          });
        } catch (err) {
          console.error('Failed to mark form as submitted:', err);
          // Don't fail the whole operation if this fails
        }
      }

      toast.success('Question submitted to your team!');
      setSubmitted(true);
    } catch (error) {
      toast.error('Failed to submit question');
      console.error('Error submitting question:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAnswerClick = () => {
    // TODO: Implement answer functionality
    toast('Answer feature coming soon!', { icon: '📝' });
  };

  // Show submitted state
  if (submitted) {
    return (
      <div className="mt-4 pt-4 border-t border-gray-700">
        <div className="flex items-center gap-2 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-xl">
          <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-green-400 font-medium">
            Question submitted to your team
          </p>
        </div>
      </div>
    );
  }

  // Show form
  return (
    <div className="mt-4 pt-4 border-t border-gray-700">
      <form onSubmit={handleSubmitQuestion} className="w-fit max-w-full">
        {/* Header with Answer button */}
        <div className="flex items-center gap-2 mb-3 whitespace-nowrap">
          <h4 className="text-xs font-semibold text-gray-400">Please submit a question to your team</h4>
          <button
            type="button"
            onClick={handleAnswerClick}
            className="px-3 py-1 text-xs font-semibold bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 rounded-lg transition-all duration-200 shadow-lg shadow-blue-600/30 flex-shrink-0"
            style={{ color: 'var(--color-text-on-accent)' }}
          >
            Answer
          </button>
        </div>

        {/* Auto-expanding Input with Submit Button Inside */}
        <div className="relative flex items-center">
          <textarea
            ref={textareaRef}
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="Type your question here..."
            rows={1}
            className="w-full min-w-[300px] px-4 py-3 pr-12 bg-white/5 border border-white/10 rounded-xl text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:bg-white/[0.07] focus:border-purple-400/50 transition-all resize-none overflow-hidden"
            style={{ minHeight: '44px' }}
            disabled={isSubmitting}
          />
          <div className="absolute right-2">
            <SendButton
              disabled={!questionText.trim() || isSubmitting}
              compact={true}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>
      </form>
    </div>
  );
}

UnknownKnowledgeForm.propTypes = {
  suggestedQuestion: PropTypes.string,
  conversationId: PropTypes.string,
  messageIndex: PropTypes.number,
  isSubmitted: PropTypes.bool,
};
